'use client';

import { useEffect, useRef } from 'react';
import { AppBusEventType } from '../interfaces/bus-events';
import Utils from '../utils/utils';
import { dispatch } from './use-bus';

export type LinkType = 'deepLink' | 'standard' | 'overwolfStore';
export interface IDeepLinkResult {
  linkType: LinkType;
  url: string;
}
export interface IDeepLinkActions {
  downloadApp: (
    url: string,
    extensionId: string,
  ) => Promise<IDeepLinkResult>;
  uninstallApp: (extensionId: string) => Promise<IDeepLinkResult>;
  openSettings: (settingsTab: string) => Promise<IDeepLinkResult>;
  syncExtensions: () => Promise<IDeepLinkResult>;
}
// -----------------------------------------------------------------------------
export const useDeepLink = (): IDeepLinkActions => {
  const fallbackTimeout = useRef<NodeJS.Timeout>(null);
  const deepLinkTimeout = useRef<NodeJS.Timeout>(null);
  const refreshInstalledAppsTimeout = useRef<NodeJS.Timeout>(null);

  // ---------------------------------------------------------------------------
  const onBlur = (): void => {
    if (!fallbackTimeout.current) {
      return;
    }

    clearTimeout(fallbackTimeout.current);
  };

  // ---------------------------------------------------------------------------
  useEffect(() => {
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('blur', onBlur);
      clearTimeout(fallbackTimeout.current);
      clearTimeout(deepLinkTimeout.current);
      clearTimeout(refreshInstalledAppsTimeout.current);
    };
  }, []);

  // ---------------------------------------------------------------------------
  const scheduleInstalledExtensionsRefresh = (): void => {
    clearTimeout(refreshInstalledAppsTimeout.current);

    refreshInstalledAppsTimeout.current = setTimeout(() => {
      dispatch({ type: AppBusEventType.App.RefreshInstalledExtensions });
    }, 5000);
  };

  // ---------------------------------------------------------------------------
  const resolveDeepLink = (
    deepLinkURL: string,
    fallbackURL: string = null,
  ): Promise<IDeepLinkResult> => {
    return new Promise((resolve) => {
      window.location.href = deepLinkURL.toString();
      fallbackTimeout.current = setTimeout(() => {
        if (fallbackURL) {
          window.location.href = fallbackURL;
        }

        resolve({
          linkType: 'standard',
          url: fallbackURL ?? window.location.href,
        });
        clearTimeout(deepLinkTimeout.current);
      }, 500);

      deepLinkTimeout.current = setTimeout(() => {
        resolve({ linkType: 'deepLink', url: deepLinkURL });
      }, 600);
    });
  };

  // ---------------------------------------------------------------------------
  const downloadApp = async (
    url: string,
    extensionId: string,
  ): Promise<IDeepLinkResult> => {
    const deepLinkURL = Utils.convertURLToDeepLink(url, extensionId);
    if (!deepLinkURL) {
      window.location.href = url;
      return Promise.resolve({ linkType: 'standard', url });
    }

    const result = await resolveDeepLink(deepLinkURL, url);
    if (result.linkType === 'deepLink') {
      scheduleInstalledExtensionsRefresh();
    }

    return result;
  };

  // ---------------------------------------------------------------------------
  const uninstallApp = async (
    extensionId: string,
  ): Promise<IDeepLinkResult> => {
    const deepLinkURL = `overwolf://uninstall/${extensionId}`;
    const result = await resolveDeepLink(deepLinkURL);
    if (result.linkType === 'deepLink') {
      scheduleInstalledExtensionsRefresh();
    }

    return result;
  };

  // ---------------------------------------------------------------------------
  const openSettings = (settingsTab: string): Promise<IDeepLinkResult> => {
    const deepLinkURL = `overwolf://settings/${settingsTab}`;
    return resolveDeepLink(deepLinkURL);
  };

  // ---------------------------------------------------------------------------
  const syncExtensions = async (): Promise<IDeepLinkResult> => {
    const deepLinkURL = 'overwolf://extensions/sync';
    const result = await resolveDeepLink(deepLinkURL);
    if (result.linkType === 'deepLink') {
      scheduleInstalledExtensionsRefresh();
    }

    return result;
  };

  return {
    downloadApp,
    uninstallApp,
    openSettings,
    syncExtensions,
  };
};
